import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Form, Modal } from "react-bootstrap";
import Dropdown from "@components/form/Dropdown.component";
import PipelineNodeSelector from "@components/pipelineNodes/PipelineNodeSelector.component";
import { getGroupValueForNodeType } from "@services/modeling.service";
import { savePipelineNode } from "@stores/data.store";
import { getErrorMessage } from "@services/errors.service";
import toast from "@services/toast.service";
import { useNavigate } from "react-router-dom";

interface NewCleaningStepModalProps {
    show: boolean;
    onHide: () => void;
    selectedNodeToClean?: string;
}

const NewCleaningStepModal: React.FC<NewCleaningStepModalProps> = ({ show, onHide, selectedNodeToClean: preSelectedNodeToClean }) => {

    const navigate = useNavigate();
    const [newCleaningStepName, setNewCleaningStepName] = useState("");
    const [selectedCleaningStepType, setSelectedCleaningStepType] = useState("WIZARD");
    const [selectedNodeToClean, setSelectedNodeToClean] = useState(preSelectedNodeToClean || ""); // Initialize with the prop if provided

    const cleaningStepOptions = [{
        label: 'Start from scratch',
        value: 'STAGING',
        description: 'Start from a clean slate using our familiar mapping interface'
    }, {
        label: 'DBT Model',
        value: 'CUSTOM',
        description: 'Write and execute custom DBT code right from Pliable',
    }, {
        label: 'Special: SPLIT',
        value: 'SPLIT',
        description: 'Split records into multiple values based on a delimiter or JSON array',
    }, {
        label: 'Use the cleaning wizard',
        value: 'WIZARD',
        description: 'Clean up source data using the Cleaning Wizard',
    }];

    const canClean = useMemo(() => {
        return (
            newCleaningStepName &&
            selectedCleaningStepType &&
            (selectedCleaningStepType !== "WIZARD" || selectedNodeToClean)
        );
    }, [newCleaningStepName, selectedCleaningStepType, selectedNodeToClean]);

    const saveCleaningStep = useCallback(async () => {
        try {
            if (["STAGING", "CUSTOM", "SPLIT"].includes(selectedCleaningStepType)) {
                let upstreamNodeIds: string[] = [];
                if ((selectedCleaningStepType === "STAGING" || selectedCleaningStepType === "SPLIT") && selectedNodeToClean) {
                    upstreamNodeIds = [selectedNodeToClean];
                }
                const newNodeData = {
                    id: null,
                    node_type: selectedCleaningStepType,
                    name: newCleaningStepName,
                    label: newCleaningStepName,
                    description: "",
                    upstream_node_ids: upstreamNodeIds,
                    fields: [],
                    table_name: "",
                };
                const newNode = await savePipelineNode(newNodeData);
                navigate("/node/" + newNode.id + "/config");
            } else {
                navigate(`/wizard/cleaning/${selectedNodeToClean}?name=${encodeURIComponent(newCleaningStepName)}`);
            }
            onHide();
        } catch (err) {
            toast("danger", "Error", getErrorMessage(err));
        }
    }, [selectedCleaningStepType, onHide, newCleaningStepName, navigate, selectedNodeToClean]);

    useEffect(() => {
        if (preSelectedNodeToClean) {
            setSelectedNodeToClean(preSelectedNodeToClean);
        }
    }, [preSelectedNodeToClean]);

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>New Cleaning Step</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label>Name your cleaning step</Form.Label>
                    <Form.Control
                        type="text"
                        value={newCleaningStepName}
                        onChange={(e) => setNewCleaningStepName(e.currentTarget.value)}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>How do you want to clean your data?</Form.Label>
                    <Dropdown
                        options={cleaningStepOptions}
                        selected={selectedCleaningStepType}
                        onChange={(val) => setSelectedCleaningStepType(val)}
                    />
                </Form.Group>
                {selectedCleaningStepType === "WIZARD" && !preSelectedNodeToClean && (
                    <Form.Group className="mb-3">
                        <Form.Label>Select table to clean</Form.Label>
                        <PipelineNodeSelector
                            selectedId={selectedNodeToClean}
                            onSelect={(node) => setSelectedNodeToClean(node?.id ?? "")}
                            optionFilter={(node) =>
                                node.node_type === "SOURCE" || getGroupValueForNodeType(node.node_type) === "STAGING"
                            }
                        />
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-light" onClick={onHide}>
                    Cancel
                </button>
                <button className="btn btn-primary" disabled={!canClean} onClick={saveCleaningStep}>
                    Start cleaning
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default NewCleaningStepModal;